import { useEffect, useState } from 'react';

const navLinks = [
  { label: '索引总览', href: '#index' },
  { label: '短讯速览', href: '#feed' },
  { label: '热度榜单', href: '#ranking' },
  { label: '专题合辑', href: '#topics' },
  { label: '指引目录', href: '#guide' },
  { label: '收录统计', href: '#overview' },
];

type MastheadProps = {
  query: string;
  onQueryChange: (value: string) => void;
};

export default function Masthead({ query, onQueryChange }: MastheadProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const solid = scrolled || menuOpen;
  const brandText = solid ? 'text-foreground-950' : 'text-background-50';
  const navText = solid ? 'text-foreground-700 hover:text-primary-600' : 'text-background-50/80 hover:text-background-50';

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid ? 'bg-background-50 border-b border-background-200' : 'bg-transparent'
      }`}
    >
      <div
        className={`hidden md:block overflow-hidden transition-all duration-500 ${
          solid ? 'max-h-0 opacity-0' : 'max-h-12 opacity-100'
        }`}
      >
        <div className="flex h-9 items-center justify-between px-4 md:px-8 text-[11px] tracking-wide text-background-50/70">
          <span>2026年9月10日 · 星期四 · 内容索引日报</span>
          <div className="flex items-center gap-5">
            <span>
              今日已更新 <strong className="font-label font-medium text-accent-300">218</strong> 条
            </span>
            <a href="#overview" className="cursor-pointer transition-colors hover:text-background-50">
              收录说明
            </a>
            <a href="#guide" className="cursor-pointer transition-colors hover:text-background-50">
              指引目录
            </a>
          </div>
        </div>
      </div>

      <div className="flex h-16 items-center justify-between gap-4 px-4 md:h-[74px] md:px-8">
        <a href="#top" className="flex shrink-0 items-center gap-3 cursor-pointer">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 font-heading text-xl font-bold text-background-50">
            料
          </span>
          <h1 className={`font-heading text-xl font-bold leading-none tracking-tight md:text-2xl ${brandText}`}>
            黑料一区
            <span
              className={`mt-1 block font-label text-[10px] font-normal tracking-[0.3em] ${
                solid ? 'text-foreground-500' : 'text-background-100/70'
              }`}
            >
              INDEX PORTAL
            </span>
          </h1>
        </a>

        <nav className="hidden items-center gap-7 text-sm lg:flex">
          {navLinks.map((link) => (
            <a key={link.href} href={link.href} className={`cursor-pointer transition-colors whitespace-nowrap ${navText}`}>
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div
            className={`hidden h-10 items-center gap-2 rounded-md border px-3 transition-colors md:flex ${
              solid ? 'border-background-300 bg-background-100' : 'border-background-50/25 bg-background-950/25'
            }`}
          >
            <span className={`flex h-5 w-5 items-center justify-center ${solid ? 'text-foreground-500' : 'text-background-100/80'}`}>
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              placeholder="搜索标题 / 分类"
              className={`w-36 bg-transparent text-sm outline-none lg:w-44 ${
                solid ? 'text-foreground-950 placeholder:text-foreground-400' : 'text-background-50 placeholder:text-background-100/60'
              }`}
            />
          </div>
          <a
            href="#feed"
            className="hidden h-10 items-center rounded-md bg-primary-500 px-4 text-sm text-background-50 whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600 md:inline-flex"
          >
            进入索引
          </a>
          <button
            type="button"
            aria-label="打开导航"
            onClick={() => setMenuOpen((prev) => !prev)}
            className={`flex h-10 w-10 items-center justify-center rounded-md border cursor-pointer transition-colors lg:hidden ${
              solid ? 'border-background-300 text-foreground-900' : 'border-background-50/30 text-background-50'
            }`}
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
          </button>
        </div>
      </div>

      {menuOpen ? (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
          <div className="flex h-10 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3">
            <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              placeholder="搜索标题 / 分类"
              className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
            />
          </div>
          <nav className="mt-3 grid grid-cols-2 gap-2">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="rounded-md border border-background-200 bg-background-100 px-3 py-2 text-sm text-foreground-800 cursor-pointer transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                {link.label}
              </a>
            ))}
          </nav>
        </div>
      ) : null}
    </header>
  );
}